import React, { useMemo } from 'react';
import { Select } from 'antd';
import { getIdMap } from '../../../helpers/helpers';
import { applyFieldMappingChange, getFieldSelectOptions } from './selectorHelpers';
import PropTypes from 'prop-types';
import { SOURCE_SELECTOR_MAX_WIDTH } from './selectorHelpers';

export default function WorkflowConfigureMultipleSourceSelector({
  fieldMappings,
  defaultValue,
  onFieldMappingsChange,
  targetId,
  eligibleSourceFields = [],
  formId,
  maxWidth = SOURCE_SELECTOR_MAX_WIDTH,
}) {
  const sourceFieldMap = useMemo(() => getIdMap(eligibleSourceFields), [eligibleSourceFields]);

  // Normalize multi-select value to array
  const multiSelectValue = useMemo(() => {
    const raw = fieldMappings[targetId] ?? defaultValue;
    if (raw == null) return [];
    return Array.isArray(raw) ? raw : [raw];
  }, [fieldMappings, targetId, defaultValue]);

  // For multi-select: filter visible options based on what is already selected to enforce type consistency.
  // - Static text selected: show only static text options
  // - Non-static-text (dropdown) selected: show only options already selected
  // - Nothing selected: show all eligible
  const visibleEligibleFields = useMemo(() => {
    if (!multiSelectValue.length) return eligibleSourceFields;
    const hasStaticText = multiSelectValue.some((id) => sourceFieldMap[id]?.selectedType === 'staticText');
    const hasNonStaticText = multiSelectValue.some((id) => {
      const f = sourceFieldMap[id];
      return f && f.selectedType !== 'staticText';
    });

    if (hasStaticText) {
      return eligibleSourceFields.filter(({ selectedType }) => selectedType === 'staticText');
    }
    if (hasNonStaticText) {
      return eligibleSourceFields.filter(({ id }) => multiSelectValue.includes(id));
    }
    return eligibleSourceFields;
  }, [eligibleSourceFields, multiSelectValue, sourceFieldMap]);

  return (
    <Select
      mode="multiple"
      placeholder="Select source field"
      dropdownMatchSelectWidth={false}
      style={{ width: '100%', maxWidth }}
      allowClear
      value={multiSelectValue}
      onChange={(selectedIds) => applyFieldMappingChange({
        fieldMappings,
        targetId,
        value: selectedIds,
        onFieldMappingsChange,
        formId,
      })}
    >
      {getFieldSelectOptions(visibleEligibleFields)}
    </Select>
  );
}


WorkflowConfigureMultipleSourceSelector.propTypes = {
  fieldMappings: PropTypes.object.isRequired,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onFieldMappingsChange: PropTypes.func.isRequired,
  targetId: PropTypes.string.isRequired,
  formId: PropTypes.string,
  eligibleSourceFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  maxWidth: PropTypes.number,
};

WorkflowConfigureMultipleSourceSelector.defaultProps = {
  defaultValue: null,
  eligibleSourceFields: [],
  maxWidth: SOURCE_SELECTOR_MAX_WIDTH,
};